/**
 * MobileWasteTracking.tsx
 *
 * Step-by-step waste tracking wizard for the 5" Munbyn handheld scanner.
 * Flow: Disposable → Human Error → By-Product → Remainder Not Intact → Intact Boxes (scan) → Summary → Supervisor Approval
 * Navigated to from MobilePackerView "Complete Session" button.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useBarcodeScanner } from '../../hooks/useBarcodeScanner';
import operatorService from '../../services/operatorService';
import inventoryService from '../../services/inventoryService';
import byproductMappingService, { ByproductMapping } from '../../services/byproductMappingService';
import { isYieldFallback } from '../../hooks/useYieldFactor';
import { calculateYieldRate, calculateWaterLiquidLoss, calculateTotalWaste, calculatePace } from '@tnb/yield-formula';
import { formatElapsedTime } from '../../utils/dateUtils';
import { useYieldThresholds } from '../../hooks/useYieldThresholds';
import { loadMobileState, clearMobileState, saveWasteWizardState, loadWasteWizardState, clearWasteWizardState, ByproductEntry } from '../../utils/mobileSessionStorage';
import { parseGS1128Barcode, isBadgeCode, extractEmployeeNumber, normalizeBadgeCode } from '../../utils/barcodeUtils';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
  X,
  ScanLine,
  ShieldCheck,
  Menu,
  Globe,
  Plus,
} from 'lucide-react';
import { Language } from '../../i18n';
import type { EmployeeInfo, SessionInfo } from '../../types';
import { StationInfo, MaterialInfo, ScannedBox, WizardStep, STEP_ORDER, STEP_LABELS } from './wasteTrackingTypes';
import { validateRemainderCaseWeight, CaseWeightVerdict } from './caseToleranceUtils';
import ByProductStep from './components/ByProductStep';
import WasteSummaryStep, { MarketableBufferInfo, FinishProductCarryover } from './components/WasteSummaryStep';
import SupervisorApprovalStep from './components/SupervisorApprovalStep';

// --- Component ---

const MobileWasteTracking: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const { yieldThreshold } = useYieldThresholds();

  // Language display names
  const languageNames: Record<Language, string> = {
    en: 'English',
    'zh-TW': '中文',
    es: 'Español',
  };
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation state from MobilePackerView, fall back to sessionStorage on refresh
  const navState = location.state as {
    session?: SessionInfo;
    station?: StationInfo;
    employee?: EmployeeInfo;
    boxCount?: number;
    isEarlyCompletion?: boolean;
  } | null;
  const savedSessionState = !navState?.session ? loadMobileState() : null;
  // Always check for saved wizard state - if user already submitted waste and goes back, resume from summary
  const savedWizardState = loadWasteWizardState();

  const session = navState?.session || savedSessionState?.session as SessionInfo || null;
  const station = navState?.station || savedSessionState?.station as StationInfo || null;
  const employee = navState?.employee || savedSessionState?.employee as EmployeeInfo || null;
  const packedBoxCount = navState?.boxCount ?? savedSessionState?.boxCount ?? 0;
  const isEarlyCompletion = navState?.isEarlyCompletion || false;

  // Wizard state - restore from sessionStorage if available
  const [currentStep, setCurrentStep] = useState<WizardStep>(
    (savedWizardState?.currentStep as WizardStep) || 'finish_product_not_intact'
  );

  // Weight values - restore from sessionStorage if available
  const [disposableWeight, setDisposableWeight] = useState(savedWizardState?.disposableWeight || '');
  const [humanErrorWeight, setHumanErrorWeight] = useState(savedWizardState?.humanErrorWeight || '');
  // Dynamic by-product entry arrays (multiple items per type)
  // Migrate from legacy single-value state if needed
  const [byproductMarketableEntries, setByproductMarketableEntries] = useState<ByproductEntry[]>(() => {
    if (savedWizardState?.byproductMarketableEntries?.length) return savedWizardState.byproductMarketableEntries;
    // Backward compat: migrate old single-value fields to array
    const oldWeight = savedWizardState?.byProductMarketableWeight || savedWizardState?.byProductWeight || '';
    const oldItem = savedWizardState?.selectedByproductMarketable || '';
    if (oldWeight || oldItem) return [{ byproductItemNumber: oldItem, weight: oldWeight }];
    return [];
  });
  const [byproductProductionEntries, setByproductProductionEntries] = useState<ByproductEntry[]>(() => {
    if (savedWizardState?.byproductProductionEntries?.length) return savedWizardState.byproductProductionEntries;
    const oldWeight = savedWizardState?.byProductProductionWeight || '';
    const oldItem = savedWizardState?.selectedByproductProduction || '';
    if (oldWeight || oldItem) return [{ byproductItemNumber: oldItem, weight: oldWeight }];
    return [];
  });
  const [remainderNotIntactWeight, setRemainderNotIntactWeight] = useState(savedWizardState?.remainderNotIntactWeight || '');
  const [finishProductNotIntactWeight, setFinishProductNotIntactWeight] = useState(savedWizardState?.finishProductNotIntactWeight || '');

  // Per-material selection for returns - restore from sessionStorage
  const [materials, setMaterials] = useState<MaterialInfo[]>([]);
  const [selectedMaterialByProductMarketable, setSelectedMaterialByProductMarketable] = useState(savedWizardState?.selectedMaterialByProductMarketable || savedWizardState?.selectedMaterialByProduct || '');
  const [selectedMaterialByProductProduction, setSelectedMaterialByProductProduction] = useState(savedWizardState?.selectedMaterialByProductProduction || '');
  const [selectedMaterialNotIntact, setSelectedMaterialNotIntact] = useState(savedWizardState?.selectedMaterialNotIntact || '');
  const [selectedMaterialIntact, setSelectedMaterialIntact] = useState(savedWizardState?.selectedMaterialIntact || '');

  // By-product mapping state - linked by-products from byproduct_raw_material_mapping
  const [byproductMappings, setByproductMappings] = useState<ByproductMapping[]>([]);

  // Marketable byproduct buffer info (carry-forward from prior sessions)
  const [marketableBufferInfo, setMarketableBufferInfo] = useState<MarketableBufferInfo[]>([]);
  const [isLoadingBufferInfo, setIsLoadingBufferInfo] = useState(false);

  // Finish-product-NI carryover allocated to this sub-session (WCO-2116)
  const [finishProductCarryover, setFinishProductCarryover] = useState<FinishProductCarryover[]>([]);

  // Intact box scanning - restore from sessionStorage
  const [scannedBoxes, setScannedBoxes] = useState<ScannedBox[]>(savedWizardState?.scannedBoxes || []);
  // Guard ref: prevents the same barcode from being processed twice within 500 ms
  // (guards against any residual duplicate event from the global useBarcodeScanner hook)
  const lastScanRef = useRef<{ barcode: string; ts: number } | null>(null);
  // Manual add form state (per-case entry for intact boxes)
  const [showManualIntact, setShowManualIntact] = useState(false);
  const [manualIntactCaseWeight, setManualIntactCaseWeight] = useState('');

  // Supervisor approval
  const [supervisorBadge, setSupervisorBadge] = useState('');
  const [isValidatingSupervisor, setIsValidatingSupervisor] = useState(false);

  // Early completion reason (only used when isEarlyCompletion is true)
  const [earlyCompletionReason, setEarlyCompletionReason] = useState('');

  // Fresh actual_output_weight from DB (session prop may use stale boxCount × unitWeight)
  const [freshOutputWeight, setFreshOutputWeight] = useState<number | null>(null);

  // Submission guard: ref so concurrent invocations are blocked synchronously
  const isSubmittingRef = useRef(false);

  // Side menu
  const [showSideMenu, setShowSideMenu] = useState(false);

  // Scanner input ref
  const scannerInputRef = useRef<HTMLInputElement>(null);
  const supervisorInputRef = useRef<HTMLInputElement>(null);

  // Redirect if no state
  useEffect(() => {
    if (!session || !station || !employee) {
      navigate('/mobile/clock-in');
    }
  }, [session, station, employee, navigate]);

  // Fetch fresh actual_output_weight from DB to get real packed weight (not boxCount × unitWeight).
  // WCO-2403: keyed by the sub_session_number on screen, NOT getMySession by employee —
  // a packer who is also the scheduled operator of another sub-session would otherwise
  // get that other sub-session's output weight here.
  useEffect(() => {
    if (!session?.sub_session_number) return;
    operatorService.getSubSessionDetails(session.sub_session_number)
      .then(result => {
        if (result.session?.actual_output_weight != null) {
          setFreshOutputWeight(parseFloat(String(result.session.actual_output_weight)));
        }
      })
      .catch(() => { /* fall back to session prop value */ });
  }, [session?.sub_session_number]);

  // Load materials for the sub-session and fetch by-product mappings
  useEffect(() => {
    if (!session) return;
    const loadMaterials = async () => {
      try {
        const result = await operatorService.getSessionMaterials(session.sub_session_number, true);
        if (result.materials && result.materials.length > 0) {
          // Deduplicate by item_number (multiple transfers can create duplicate entries)
          const seen = new Set<string>();
          const uniqueMaterials = result.materials.filter((m: MaterialInfo) => {
            if (seen.has(m.item_number)) return false;
            seen.add(m.item_number);
            return true;
          });
          setMaterials(uniqueMaterials);
          setSelectedMaterialByProductMarketable(uniqueMaterials[0].item_number);
          setSelectedMaterialByProductProduction(uniqueMaterials[0].item_number);
          setSelectedMaterialNotIntact(uniqueMaterials[0].item_number);
          setSelectedMaterialIntact(uniqueMaterials[0].item_number);

          // Fetch by-product mappings for all session materials
          try {
            const allMappings = await byproductMappingService.getMappings();
            const materialItemNumbers = uniqueMaterials.map((m: MaterialInfo) => m.item_number);
            const filtered = allMappings.filter(m => materialItemNumbers.includes(m.raw_material_item_number));
            setByproductMappings(filtered);
          } catch (mappingErr) {
            console.error('Failed to load by-product mappings:', mappingErr);
          }
        }
      } catch (err) {
        console.error('Failed to load materials:', err);
      }
    };
    loadMaterials();
  }, [session]);

  // Load finish-product-NI carryover allocated to this sub-session
  useEffect(() => {
    if (!session) return;
    operatorService.getFinishProductCarryover(session.sub_session_number)
      .then(result => setFinishProductCarryover(result.carryover || []))
      .catch(err => console.error('Failed to load finish-product carryover:', err));
  }, [session]);

  // Persist wizard state to sessionStorage so it survives browser refresh
  useEffect(() => {
    if (currentStep !== 'submitting' && currentStep !== 'completed') {
      saveWasteWizardState({
        currentStep,
        disposableWeight,
        humanErrorWeight,
        byproductMarketableEntries,
        byproductProductionEntries,
        remainderNotIntactWeight,
        finishProductNotIntactWeight,
        scannedBoxes,
        selectedMaterialByProductMarketable,
        selectedMaterialByProductProduction,
        selectedMaterialNotIntact,
        selectedMaterialIntact,
      });
    }
  }, [currentStep, disposableWeight, humanErrorWeight, byproductMarketableEntries, byproductProductionEntries, remainderNotIntactWeight, finishProductNotIntactWeight, scannedBoxes, selectedMaterialByProductMarketable, selectedMaterialByProductProduction, selectedMaterialNotIntact, selectedMaterialIntact]);

  // Focus scanner input on intact boxes step
  useEffect(() => {
    if (currentStep === 'intact_boxes' && scannerInputRef.current) {
      setTimeout(() => scannerInputRef.current?.focus(), 200);
    }
  }, [currentStep]);

  // Focus supervisor input
  useEffect(() => {
    if (currentStep === 'supervisor_approval' && supervisorInputRef.current) {
      setTimeout(() => supervisorInputRef.current?.focus(), 200);
    }
  }, [currentStep]);

  if (!session || !station || !employee) return null;

  // --- Calculations ---

  const initialMaterial = parseFloat(String(session.initial_material_weight)) || 0;
  const totalOutput = freshOutputWeight ?? (parseFloat(String(session.actual_output_weight)) || 0);

  const disposable = parseFloat(disposableWeight) || 0;
  const humanError = parseFloat(humanErrorWeight) || 0;
  // By-product totals: sum of all entries' weights per type
  const byProductMarketable = byproductMarketableEntries.reduce((sum, e) => sum + (parseFloat(e.weight) || 0), 0);
  const byProductProduction = byproductProductionEntries.reduce((sum, e) => sum + (parseFloat(e.weight) || 0), 0);
  const byProductTotal = byProductMarketable + byProductProduction;
  const remainderNotIntact = parseFloat(remainderNotIntactWeight) || 0;
  const finishProductNotIntact = parseFloat(finishProductNotIntactWeight) || 0;
  const remainderIntact = scannedBoxes.reduce((sum, box) => sum + box.weight, 0);
  const fpNotIntactFromLot = finishProductCarryover.reduce(
    (sum, c) => sum + (Number(c.allocated_weight) || 0),
    0
  );

  const waterLiquidRaw = calculateWaterLiquidLoss({
    initialRawMaterial: initialMaterial,
    actualOutputWeight: totalOutput,
    fpNotIntactFromLot,
    fpNotIntactNewGenerated: finishProductNotIntact,
    disposable,
    humanError,
    byProductTotal,
    rawMaterialNotIntact: remainderNotIntact,
    remainderIntact,
  });
  const waterLiquid = Math.max(0, waterLiquidRaw);
  const waterLiquidNegative = waterLiquidRaw < 0;

  const totalWaste = calculateTotalWaste({ disposable, humanError, waterLiquid });

  // Yield as decimal (e.g. 0.922 = 92.2%). Source of truth: @tnb/yield-formula.
  const yieldRate = Math.min(9.9999, calculateYieldRate({
    actualOutputWeight: totalOutput,
    fpNotIntactFromLot,
    fpNotIntactNewGenerated: finishProductNotIntact,
    initialRawMaterial: initialMaterial,
    remainderIntact,
    rawMaterialNotIntact: remainderNotIntact,
    humanError,
  }) / 100);

  // Expected yield from session (already decimal from service layer, e.g. 0.85)
  const effectiveYield = Number(session?.raw_material_yield) || 0;
  const hasYieldFactor = effectiveYield > 0;
  const expectedYield = effectiveYield;
  const isFallbackYield = isYieldFallback(session?.raw_material_yield_source);
  const yieldVariance = hasYieldFactor ? Math.abs(yieldRate - expectedYield) : 0;
  const yieldOutsideTolerance = hasYieldFactor && yieldThreshold !== null && yieldVariance > yieldThreshold;

  // Elapsed time and pace calculations
  const getElapsedHours = () => {
    if (!session?.started_at) return 0;
    const startTime = new Date(session.started_at).getTime();
    const now = Date.now();
    const totalElapsedMs = now - startTime;
    const pausedMs = (session.paused_duration_seconds || 0) * 1000;
    const effectivePausedMs = Math.min(pausedMs, totalElapsedMs);
    const workingMs = totalElapsedMs - effectivePausedMs;
    return Math.max(0, workingMs) / (1000 * 60 * 60);
  };
  const elapsedHours = getElapsedHours();
  const boxCount = session?.total_boxes || packedBoxCount || 0;
  const pace = calculatePace({ boxCount, elapsedHours });

  const elapsedTimeFormatted = formatElapsedTime(elapsedHours);

  // Progress percentage based on box count (matches Packer View)
  // Calculate expected boxes from target_output_weight / unit_weight if not provided
  // Try multiple sources for unit weight: unit_weight, or derive from actual_output / boxes packed
  const unitWeight = Number(session?.product_unit_weight || session?.unit_weight) ||
                     (boxCount > 0 && totalOutput > 0 ? totalOutput / boxCount : 0);
  const targetWeight = Number(session?.target_output_weight) || 0;
  const calculatedExpectedBoxes = unitWeight > 0 ? Math.floor(targetWeight / unitWeight) : 0;
  const expectedBoxes = Number(session?.expected_boxes) || calculatedExpectedBoxes;
  const progressPct = expectedBoxes > 0 ? (boxCount / expectedBoxes) * 100 : 0;

  // --- Fetch Marketable Buffer Info ---
  // Called when entering the summary step to show carry-forward breakdown
  const fetchMarketableBufferInfo = useCallback(async () => {
    const sourceMaterial = selectedMaterialByProductMarketable || materials[0]?.item_number;
    // Only fetch if there are marketable byproduct entries with valid items
    const validEntries = byproductMarketableEntries.filter(e => e.byproductItemNumber);
    if (validEntries.length === 0 || !sourceMaterial) {
      setMarketableBufferInfo([]);
      return;
    }

    setIsLoadingBufferInfo(true);
    try {
      const batchItems = Array.from(new Map(
        validEntries.map((entry) => {
          const key = `${sourceMaterial}::${entry.byproductItemNumber}`;
          return [key, {
            sourceMaterialItemNumber: sourceMaterial,
            byproductItemNumber: entry.byproductItemNumber,
          }];
        })
      ).values());

      const { buffers } = await operatorService.getMarketableBufferStateBatch(batchItems);
      const bufferInfoList: MarketableBufferInfo[] = batchItems.map((item) => {
        const key = `${item.sourceMaterialItemNumber}::${item.byproductItemNumber}`;
        const result = buffers[key];

        return {
          byproductItemNumber: item.byproductItemNumber,
          sourceMaterialItemNumber: item.sourceMaterialItemNumber,
          reservedWeight: result?.reserved_weight ?? 0,
          readyCaseCount: result?.ready_case_count ?? 0,
          readyCaseWeight: result?.ready_case_weight ?? 0,
          totalBufferWeight: result?.total_buffer_weight ?? 0,
          lastSessionNumber: result?.last_session_number ?? null,
        };
      });

      setMarketableBufferInfo(bufferInfoList);
    } catch (err) {
      console.error('Failed to fetch marketable buffer info:', err);
      // Don't block the flow - just show without buffer info
      setMarketableBufferInfo([]);
    } finally {
      setIsLoadingBufferInfo(false);
    }
  }, [byproductMarketableEntries, selectedMaterialByProductMarketable, materials]);

  // --- Navigation ---

  const currentStepIndex = STEP_ORDER.indexOf(currentStep);

  const goNext = useCallback(async () => {
    if (currentStep === 'summary') {
      // Always require supervisor sign-off for session completion
      setCurrentStep('supervisor_approval');
      return;
    }

    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEP_ORDER.length) {
      const nextStep = STEP_ORDER[nextIndex];

      // If moving to summary, fetch buffer info first
      if (nextStep === 'summary') {
        await fetchMarketableBufferInfo();
      }

      setCurrentStep(nextStep);
    }
  }, [currentStep, currentStepIndex, fetchMarketableBufferInfo]);

  const goBack = () => {
    if (currentStep === 'supervisor_approval') {
      setCurrentStep('summary');
      return;
    }
    if (currentStepIndex > 0) {
      setCurrentStep(STEP_ORDER[currentStepIndex - 1]);
    } else {
      // Go back to packer view
      navigate('/operator/mobile-packer', {
        state: { station, session, employee }
      });
    }
  };

  // WCO-2364: turn a per-case tolerance verdict into a toast; returns true when the weight is OK.
  const passesCaseTolerance = (verdict: CaseWeightVerdict): boolean => {
    if (verdict.ok) return true;
    if (verdict.code === 'zero') {
      toast.error(t('mobile.caseWeightMustBePositive') || 'Case weight must be greater than 0');
    } else if (verdict.code === 'above') {
      toast.error((t('mobile.caseWeightAboveMax') || 'Case weight {weight} lbs exceeds the allowed maximum of {max} lbs')
        .replace('{max}', verdict.upper.toFixed(2)));
    } else {
      toast.error((t('mobile.caseWeightOutOfRange') || 'Case weight must be between {min} and {max} lbs')
        .replace('{min}', verdict.lower.toFixed(2))
        .replace('{max}', verdict.upper.toFixed(2)));
    }
    return false;
  };

  // --- Intact Box Scanning ---

  const handleIntactBoxScan = useCallback(async (barcode: string) => {
    if (currentStep !== 'intact_boxes') return;
    // Guard: ignore if this exact barcode was already processed within the last 500 ms
    // (prevents double-counting from duplicate scan events)
    const now = Date.now();
    if (lastScanRef.current && lastScanRef.current.barcode === barcode && now - lastScanRef.current.ts < 500) {
      return;
    }
    lastScanRef.current = { barcode, ts: now };
    if (isBadgeCode(barcode)) {
      toast.error(t('mobile.scanBoxNotBadge') || 'Scan a box barcode, not a badge');
      return;
    }

    const parsed = parseGS1128Barcode(barcode);
    if (!parsed) {
      toast.error(t('mobile.invalidBarcode') || 'Invalid barcode');
      return;
    }

    // Validate GTIN against assigned materials
    const matchingMaterial = parsed.gtin ? materials.find(m => m.upc === parsed.gtin) : undefined;
    if (parsed.gtin && !matchingMaterial) {
      toast.error(t('mobile.gtinMismatch') || 'Barcode does not match assigned raw materials');
      return;
    }

    // Look up item details from backend (weight fallback + brand)
    let gtinResult: { item_name?: string; unit_weight?: number; brand?: string } | null = null;
    if (parsed.gtin) {
      try {
        gtinResult = await inventoryService.lookupGtin(parsed.gtin);
      } catch (err) {
        console.error('GTIN lookup failed:', err);
      }
    }

    // Resolve weight: barcode weight first, then database unit_weight
    const boxWeight = parsed.normalizedWeightLb ?? gtinResult?.unit_weight;
    if (!boxWeight) {
      toast.error(t('mobile.invalidBarcode') || 'Invalid barcode - could not determine weight');
      return;
    }

    // Check for duplicate scan — skip for raw materials (their barcodes only contain SKU, not a unique box ID)
    if (!matchingMaterial && scannedBoxes.some(b => b.barcode === barcode)) {
      toast.error(t('mobile.boxAlreadyScanned') || 'This box was already scanned');
      return;
    }

    // WCO-2364: intact case must fall inside the symmetric VB6 deviation band.
    if (!passesCaseTolerance(validateRemainderCaseWeight(boxWeight, matchingMaterial, true))) return;

    const newBox: ScannedBox = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      barcode,
      weight: boxWeight,
      lot: parsed.lot || 'Unknown',
      materialName: matchingMaterial?.item_name || gtinResult?.item_name,
      brand: gtinResult?.brand || undefined,
    };

    setScannedBoxes(prev => [...prev, newBox]);
    toast.success(`${boxWeight.toFixed(2)} lbs`);

    // Refocus scanner input
    setTimeout(() => scannerInputRef.current?.focus(), 100);
  }, [currentStep, scannedBoxes, t, materials]);

  useBarcodeScanner({
    onScan: handleIntactBoxScan,
    enabled: currentStep === 'intact_boxes',
    minLength: 10,
  });

  const removeBox = (id: string) => {
    setScannedBoxes(prev => prev.filter(b => b.id !== id));
  };

  // --- Manual Intact Case Add ---
  const handleManualIntactAdd = () => {
    const weight = parseFloat(manualIntactCaseWeight);
    if (isNaN(weight) || weight <= 0) return;

    const materialItem = materials.find(m => m.item_number === selectedMaterialIntact) || materials[0];

    // WCO-2364: intact case must fall inside the symmetric VB6 deviation band.
    if (!passesCaseTolerance(validateRemainderCaseWeight(weight, materialItem, true))) return;

    const newBox: ScannedBox = {
      id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      barcode: 'MANUAL',
      weight,
      lot: materialItem?.transfer_number || 'Unknown',
      materialName: materialItem?.item_name || materialItem?.item_number || 'Unknown',
      itemNumber: materialItem?.item_number,
      isManual: true,
    };

    setScannedBoxes(prev => [...prev, newBox]);
    setManualIntactCaseWeight('');
  };

  // WCO-2364: validate the (single) non-intact remainder weight before leaving the step.
  // Non-intact = upper bound + >0 only (no lower bound — opened cases are legitimately light).
  // A blank/zero entry means "no remainder" and is allowed to pass.
  const handleRemainderNotIntactNext = () => {
    const weight = parseFloat(remainderNotIntactWeight);
    if (weight > 0) {
      const material = materials.find(m => m.item_number === selectedMaterialNotIntact) || materials[0];
      if (!passesCaseTolerance(validateRemainderCaseWeight(weight, material, false))) return;
    }
    goNext();
  };

  // --- Supervisor Approval ---

  const handleSupervisorScan = useCallback(async (barcode: string) => {
    if (currentStep !== 'supervisor_approval') return;

    // If early completion, require a reason
    if (isEarlyCompletion && !earlyCompletionReason) {
      toast.error(t('mobile.selectEarlyCompletionReason') || 'Please select a reason for early completion');
      return;
    }

    const badge = normalizeBadgeCode(barcode);
    const empNumber = extractEmployeeNumber(barcode);

    setIsValidatingSupervisor(true);
    try {
      const result = await operatorService.lookupEmployee(badge);
      if (result.success && result.employee) {
        // Check supervisor role - only user_level 'S' (Supervisor) can approve waste tracking
        if (result.employee.user_level !== 'S') {
          toast.error(t('mobile.supervisorOnlyCanApprove') || 'Only Supervisors can approve waste tracking');
          setIsValidatingSupervisor(false);
          return;
        }
        toast.success(`${t('mobile.supervisorApproved') || 'Approved by'} ${result.employee.display_name}`);
        handleSubmit(empNumber);
      } else {
        toast.error(result.message || t('mobile.invalidBadge') || 'Invalid badge');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to validate badge');
    } finally {
      setIsValidatingSupervisor(false);
    }
  }, [currentStep, t, isEarlyCompletion, earlyCompletionReason]);

  useBarcodeScanner({
    onScan: handleSupervisorScan,
    enabled: currentStep === 'supervisor_approval',
    minLength: 4,
  });

  // --- Submit ---

  const handleSubmit = async (supervisorId?: string) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setCurrentStep('submitting');

    try {
      // Delete any pre-existing waste records as a safety net before inserting
      await operatorService.deleteSessionWasteRecords(session!.sub_session_number);

      // WCO-2545: carry the per-case scans for the intact remainder — each scanned box's full raw
      // barcode (or '' for a manually-added damaged case → backend MANUAL bucket). This gives the
      // transfer-out step a per-barcode tally to cap against.
      const intactCases = scannedBoxes.map((b) => ({
        barcode: b.isManual ? '' : b.barcode,
        weight: b.weight,
      }));

      // Build waste entries: fixed types + dynamic by-product entries
      const wasteEntries: Array<{ type: string; weight: number; material: string | null; quantity?: number; cases?: Array<{ barcode?: string; weight?: number }> }> = [
        { type: 'disposable', weight: disposable, material: null },
        { type: 'water_liquid', weight: waterLiquid, material: null },
        { type: 'human_error', weight: humanError, material: null },
        { type: 'remainder_not_intact', weight: remainderNotIntact, material: selectedMaterialNotIntact || null },
        { type: 'finish_product_not_intact', weight: finishProductNotIntact, material: null },
        { type: 'remainder_intact', weight: remainderIntact, material: selectedMaterialIntact || null, quantity: scannedBoxes.length, cases: intactCases },
      ];
      // Dynamic by-product entries: one waste record per entry
      for (const entry of byproductMarketableEntries) {
        const w = parseFloat(entry.weight) || 0;
        if (w > 0) wasteEntries.push({ type: 'by_product_marketable', weight: w, material: entry.byproductItemNumber || selectedMaterialByProductMarketable || null });
      }
      for (const entry of byproductProductionEntries) {
        const w = parseFloat(entry.weight) || 0;
        if (w > 0) wasteEntries.push({ type: 'by_product_production', weight: w, material: entry.byproductItemNumber || selectedMaterialByProductProduction || null });
      }

      // POST each waste type with weight > 0
      for (const entry of wasteEntries) {
        if (entry.weight > 0) {
          await operatorService.recordWaste({
            sub_session_number: session!.sub_session_number,
            waste_type: entry.type,
            waste_weight: entry.weight,
            waste_reason: 'Recorded from mobile waste tracking',
            disposal_method: entry.material || undefined,
            operator_id: employee!.user_id || employee!.employee_number,
            quantity: entry.quantity,
            cases: entry.cases,
          });
        }
      }

      // Determine reason based on early completion status and yield
      let completionReason = yieldOutsideTolerance
        ? 'Session completion - yield outside tolerance'
        : 'Session completion - yield within tolerance';

      if (isEarlyCompletion) {
        completionReason = `Early completion - ${earlyCompletionReason}`;
      }

      // Record supervisor EOD sign-off (always required for session completion)
      // For early completion, also record as session_completion override
      await operatorService.recordSupervisorOverride({
        override_type: isEarlyCompletion ? 'session_completion' : 'eod_signoff',
        supervisor_id: supervisorId!,
        supervisor_name: supervisorId!, // Will be resolved by backend
        session_number: undefined, // Let backend derive from sub_session_number
        sub_session_number: session!.sub_session_number,
        operator_employee_number: employee?.employee_number,
        operator_name: employee?.display_name,
        reason: completionReason,
        details: {
          // Early completion info
          is_early_completion: isEarlyCompletion,
          early_completion_reason: isEarlyCompletion ? earlyCompletionReason : undefined,
          progress_percentage: progressPct,
          // Yield info — store as whole % in DB for backward compat
          expected_yield: expectedYield * 100,
          actual_yield: yieldRate * 100,
          yield_variance: yieldVariance * 100,
          yield_outside_tolerance: yieldOutsideTolerance,
          // Session summary
          initial_material: initialMaterial,
          total_output: totalOutput,
          total_waste: totalWaste,
          // Waste breakdown
          waste_disposable: disposable,
          waste_human_error: humanError,
          waste_by_product_marketable: byProductMarketable,
          waste_by_product_production: byProductProduction,
          waste_remainder_not_intact: remainderNotIntact,
          waste_finish_product_not_intact: finishProductNotIntact,
          waste_remainder_intact: remainderIntact,
          waste_water_liquid: waterLiquid,
          // Boxes
          intact_boxes_count: scannedBoxes.length,
        },
      });

      // Complete the sub-session
      const notes = isEarlyCompletion
        ? `Early completion from mobile. Reason: ${earlyCompletionReason}. Supervisor sign-off: ${supervisorId}`
        : `Completed from mobile. Supervisor sign-off: ${supervisorId}`;

      await operatorService.completeSession(
        session!.sub_session_number,
        notes,
        true, // isSubSession
        yieldRate,
        isEarlyCompletion ? 'override' : 'normal' // completion_type
      );

      setCurrentStep('completed');

      // Clear persisted state since session is done
      clearWasteWizardState();
      clearMobileState();

      // Redirect to clock-in after 2 seconds
      setTimeout(() => {
        navigate('/mobile/clock-in');
      }, 2000);

    } catch (err: any) {
      console.error('Failed to submit waste:', err);
      const errorMsg = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to submit waste tracking';
      toast.error(errorMsg, { duration: 6000 });

      // If session not found or already completed, redirect to scan station
      const isFatalError = errorMsg.toLowerCase().includes('not found') ||
                          errorMsg.toLowerCase().includes('already completed') ||
                          errorMsg.toLowerCase().includes('not active');
      if (isFatalError) {
        clearWasteWizardState();
        clearMobileState();
        setTimeout(() => {
          navigate('/mobile/clock-in');
        }, 2000);
      } else {
        setCurrentStep('summary'); // Go back to summary on recoverable error
      }
      isSubmittingRef.current = false;
    }
  };

  // --- Step Indicator Dots ---

  const renderStepDots = () => (
    <div className="flex items-center justify-center gap-2 mb-4">
      {STEP_ORDER.map((step, i) => (
        <div
          key={step}
          className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-sm md:text-base font-bold transition-colors ${
            i < currentStepIndex
              ? 'bg-green-500 text-white'
              : i === currentStepIndex
                ? 'bg-amber-500 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
          }`}
        >
          {i < currentStepIndex ? '✓' : STEP_LABELS[step]}
        </div>
      ))}
    </div>
  );

  // --- Render Steps ---

  // Reusable weight input for steps 1-3
  const renderWeightInput = (
    value: string,
    onChange: (v: string) => void,
    label: string,
    description: string,
    note?: string,
    badge?: string,
  ) => (
    <div className="flex-1 flex flex-col items-center justify-center">
      <div className="w-full max-w-xs md:max-w-sm lg:max-w-md">
        <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white text-center mb-1">
          {label}
        </h3>
        <p className="text-sm md:text-base text-gray-500 text-center mb-1">{description}</p>
        {badge && (
          <p className="text-center mb-4">
            <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
              {badge}
            </span>
          </p>
        )}
        {!badge && <div className="mb-4" />}
        <div className="relative">
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="0.00"
            className="w-full text-center text-5xl md:text-6xl lg:text-7xl font-bold py-6 px-4 rounded-2xl border-4 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-amber-500 transition-colors"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xl md:text-2xl text-gray-400 font-medium">
            lbs
          </span>
        </div>
        {note && (
          <p className="text-xs text-gray-400 text-center mt-3">{note}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className={`flex flex-col bg-gray-100 dark:bg-gray-900 ${currentStep === 'intact_boxes' ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
      {/* Header */}
      <header className="bg-amber-600 dark:bg-amber-700 text-white px-4 md:px-6 lg:px-8 py-3 sticky top-0 z-10 flex items-center gap-3">
        <button
          onClick={() => setShowSideMenu(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg md:text-xl font-bold">{t('mobile.wasteTracking') || 'Waste Tracking'}</h1>
          <p className="text-xs opacity-80">{session.sub_session_number} · {station.station_name}</p>
        </div>

        {/* Supervisor badge input in header during approval step */}
        {currentStep === 'supervisor_approval' && (
          <div className="flex items-center gap-1.5 bg-blue-500/30 rounded-lg px-2 py-2 w-[120px] animate-pulse ring-2 ring-blue-400/50">
            <ShieldCheck className="w-4 h-4 text-blue-300 flex-shrink-0" />
            <input
              ref={supervisorInputRef}
              type="text"
              value={supervisorBadge}
              onChange={(e) => setSupervisorBadge(e.target.value)}
              placeholder="Badge"
              className="flex-1 bg-transparent text-white placeholder-white/70 outline-none text-sm min-w-0"
              autoComplete="off"
            />
            {isValidatingSupervisor && <Loader2 className="w-4 h-4 animate-spin text-white/70 flex-shrink-0" />}
          </div>
        )}
      </header>

      <div className="flex-1 flex flex-col min-h-0 p-4 md:p-6 lg:p-8 md:max-w-3xl lg:max-w-4xl md:mx-auto md:w-full">
        {/* Step dots (not shown during submitting/completed) */}
        {STEP_ORDER.includes(currentStep) && renderStepDots()}

        {/* === Step 1: Finish Product - Not Intact === */}
        {currentStep === 'finish_product_not_intact' && (
          <>
            {renderWeightInput(
              finishProductNotIntactWeight,
              setFinishProductNotIntactWeight,
              t('mobile.finishProductNotIntact') || 'Finish Product - Not Intact',
              t('mobile.finishProductNotIntactDesc') || "Remainder of finish product that doesn't make a full case",
            )}
            <div className="flex gap-3 mt-4">
              <button onClick={goBack} className="flex-1 py-4 md:py-5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-lg md:text-xl flex items-center justify-center gap-2">
                <ArrowLeft className="w-5 h-5" />
                {t('common.back') || 'Back'}
              </button>
              <button onClick={goNext} className="flex-1 py-4 md:py-5 bg-amber-500 text-white rounded-xl font-semibold text-lg md:text-xl flex items-center justify-center gap-2">
                {t('common.next') || 'Next'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </>
        )}

        {/* === Step 2: Disposable === */}
        {currentStep === 'disposable' && (
          <>
            {renderWeightInput(
              disposableWeight,
              setDisposableWeight,
              t('mobile.disposableWaste') || 'Disposable Waste',
              t('mobile.disposableDesc') || 'Trash, non-recoverable material',
            )}
            <div className="flex gap-3 mt-4">
              <button onClick={goBack} className="flex-1 py-4 md:py-5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-lg md:text-xl flex items-center justify-center gap-2">
                <ArrowLeft className="w-5 h-5" />
                {t('common.back') || 'Back'}
              </button>
              <button onClick={goNext} className="flex-1 py-4 md:py-5 bg-amber-500 text-white rounded-xl font-semibold text-lg md:text-xl flex items-center justify-center gap-2">
                {t('common.next') || 'Next'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </>
        )}

        {/* === Step 2: Human Error === */}
        {currentStep === 'human_error' && (
          <>
            {renderWeightInput(
              humanErrorWeight,
              setHumanErrorWeight,
              t('mobile.humanError') || 'Human Error',
              t('mobile.humanErrorDesc') || 'Does not count as waste',
              undefined,
              t('mobile.trackingOnly') || 'Tracking only',
            )}
            <div className="flex gap-3 mt-4">
              <button onClick={goBack} className="flex-1 py-4 md:py-5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-lg md:text-xl flex items-center justify-center gap-2">
                <ArrowLeft className="w-5 h-5" />
                {t('common.back') || 'Back'}
              </button>
              <button onClick={goNext} className="flex-1 py-4 md:py-5 bg-amber-500 text-white rounded-xl font-semibold text-lg md:text-xl flex items-center justify-center gap-2">
                {t('common.next') || 'Next'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </>
        )}

        {/* === Step 3: By-Product === */}
        {currentStep === 'by_product' && (
          <ByProductStep
            materials={materials}
            byproductMappings={byproductMappings}
            selectedMaterialByProductMarketable={selectedMaterialByProductMarketable}
            setSelectedMaterialByProductMarketable={setSelectedMaterialByProductMarketable}
            setSelectedMaterialByProductProduction={setSelectedMaterialByProductProduction}
            byproductMarketableEntries={byproductMarketableEntries}
            setByproductMarketableEntries={setByproductMarketableEntries}
            byproductProductionEntries={byproductProductionEntries}
            setByproductProductionEntries={setByproductProductionEntries}
            byProductMarketable={byProductMarketable}
            byProductProduction={byProductProduction}
            goBack={goBack}
            goNext={goNext}
          />
        )}

        {/* === Step 4: Remainder Not Intact === */}
        {currentStep === 'remainder_not_intact' && (
          <>
            {/* Material selector (only if multiple materials) */}
            {materials.length > 1 && (
              <div className="mb-2">
                <label className="text-sm text-gray-500 mb-1 block">
                  {t('mobile.selectMaterial') || 'Select Material'}
                </label>
                <select
                  value={selectedMaterialNotIntact}
                  onChange={(e) => setSelectedMaterialNotIntact(e.target.value)}
                  className="w-full px-3 py-3 rounded-xl border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-base"
                >
                  {materials.map(m => (
                    <option key={m.item_number} value={m.item_number}>
                      {m.item_name || m.item_number}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {renderWeightInput(
              remainderNotIntactWeight,
              setRemainderNotIntactWeight,
              t('mobile.remainderNotIntact') || 'Remainder - Not Intact',
              t('mobile.remainderNotIntactDesc') || 'Opened or partial raw materials',
            )}
            <div className="flex gap-3 mt-4">
              <button onClick={goBack} className="flex-1 py-4 md:py-5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-lg md:text-xl flex items-center justify-center gap-2">
                <ArrowLeft className="w-5 h-5" />
                {t('common.back') || 'Back'}
              </button>
              <button onClick={handleRemainderNotIntactNext} className="flex-1 py-4 md:py-5 bg-amber-500 text-white rounded-xl font-semibold text-lg md:text-xl flex items-center justify-center gap-2">
                {t('common.next') || 'Next'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </>
        )}

        {/* === Step 5: Intact Boxes (Scanning) === */}
        {currentStep === 'intact_boxes' && (
          <>
            {/* Scrollable top section */}
            <div className="flex-1 flex flex-col min-h-0">
              {/* Step title */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-0.5">
                {t('mobile.intactBoxes') || 'Intact Boxes'}
              </h3>
              <p className="text-sm text-gray-500 text-center mb-2">
                {t('mobile.intactBoxesDesc') || 'Sealed raw material boxes returned to lot'}
              </p>

              {/* KPI counters */}
              <div className="flex gap-2 mb-2">
                <div className="flex-1 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-1.5 flex items-center justify-center gap-2">
                  <p className="text-xl font-bold text-green-600 dark:text-green-400">{scannedBoxes.length}</p>
                  <p className="text-xs text-gray-500">{t('mobile.boxesScanned') || 'boxes'}</p>
                </div>
                <div className="flex-1 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-1.5 flex items-center justify-center gap-2">
                  <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{remainderIntact.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">lbs</p>
                </div>
              </div>

              {/* Scanner input row */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <ScanLine className="w-4 h-4 text-amber-500" />
                  </div>
                  <input
                    ref={scannerInputRef}
                    type="text"
                    placeholder={t('mobile.scanIntactBox') || 'Scan intact box barcode...'}
                    className="w-full pl-10 pr-3 py-3 border-2 border-dashed border-amber-400 dark:border-amber-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-base"
                  />
                </div>
                {/* Material selector (only if multiple materials) */}
                {materials.length > 1 && (
                  <select
                    value={selectedMaterialIntact}
                    onChange={(e) => setSelectedMaterialIntact(e.target.value)}
                    className="px-2 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm max-w-[40%]"
                  >
                    {materials.map(m => (
                      <option key={m.item_number} value={m.item_number}>
                        {m.item_name || m.item_number}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Manual add per-case — between scanner and boxes list */}
              {!showManualIntact ? (
                <button
                  onClick={() => setShowManualIntact(true)}
                  className="w-full py-2 mb-2 flex items-center justify-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-400 dark:hover:text-gray-300 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {t('mobile.manualAddCase') || 'Manual Add Case'}
                </button>
              ) : (
                <div className="bg-amber-50 dark:bg-amber-900/10 rounded-xl p-3 mb-2 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                      {t('mobile.manualEntry') || 'Manual Entry'}
                    </span>
                    <button
                      onClick={() => { setShowManualIntact(false); setManualIntactCaseWeight(''); }}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Material selector (if multiple materials) */}
                  {materials.length > 1 && (
                    <select
                      value={selectedMaterialIntact}
                      onChange={(e) => setSelectedMaterialIntact(e.target.value)}
                      className="w-full px-3 py-2 mb-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                    >
                      {materials.map(m => (
                        <option key={m.item_number} value={m.item_number}>
                          {m.item_name || m.item_number}
                        </option>
                      ))}
                    </select>
                  )}
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={manualIntactCaseWeight}
                        onChange={(e) => setManualIntactCaseWeight(e.target.value)}
                        placeholder="0.00"
                        className="w-full text-center text-2xl font-bold py-3 px-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-amber-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleManualIntactAdd();
                          }
                        }}
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-base text-gray-400">lbs</span>
                    </div>
                    <button
                      onClick={handleManualIntactAdd}
                      disabled={!manualIntactCaseWeight || parseFloat(manualIntactCaseWeight) <= 0}
                      className="px-5 py-3 bg-amber-500 text-white rounded-lg font-semibold text-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-600 transition-colors"
                    >
                      {t('common.add') || 'Add'}
                    </button>
                  </div>
                </div>
              )}

              {/* Scanned/manual boxes list — scrollable, fills remaining space */}
              <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
                {scannedBoxes.map((box) => (
                  <div key={box.id} className="flex items-center bg-white dark:bg-gray-800 rounded-lg px-3 py-1.5 border border-gray-200 dark:border-gray-700">
                    {box.isManual && (
                      <span className="px-1 py-0.5 text-[9px] font-bold rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0 mr-2">M</span>
                    )}
                    <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0 mr-1.5 font-mono">{box.itemNumber || (box.lot !== 'Unknown' ? box.lot : '')}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">{box.materialName || box.lot}</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0 ml-2">{box.weight.toFixed(2)} lbs</span>
                    <button onClick={() => removeBox(box.id)} className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded shrink-0 ml-1">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Fixed bottom section — navigation only */}
            <div className="shrink-0 mt-2">
              <div className="flex gap-3">
                <button onClick={goBack} className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-base flex items-center justify-center gap-2">
                  <ArrowLeft className="w-5 h-5" />
                  {t('common.back') || 'Back'}
                </button>
                <button onClick={goNext} className="flex-1 py-3 bg-amber-500 text-white rounded-xl font-semibold text-base flex items-center justify-center gap-2">
                  {t('common.next') || 'Next'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* === Step 6: Summary === */}
        {currentStep === 'summary' && (
          <WasteSummaryStep
            initialMaterial={initialMaterial}
            totalOutput={totalOutput}
            packedBoxCount={packedBoxCount}
            disposable={disposable}
            humanError={humanError}
            byproductMarketableEntries={byproductMarketableEntries}
            byproductProductionEntries={byproductProductionEntries}
            byproductMappings={byproductMappings}
            marketableBufferInfo={marketableBufferInfo}
            finishProductCarryover={finishProductCarryover}
            remainderNotIntact={remainderNotIntact}
            finishProductNotIntact={finishProductNotIntact}
            remainderIntact={remainderIntact}
            scannedBoxes={scannedBoxes}
            waterLiquid={waterLiquid}
            waterLiquidRaw={waterLiquidRaw}
            waterLiquidNegative={waterLiquidNegative}
            yieldRate={yieldRate}
            goBack={goBack}
            goNext={goNext}
          />
        )}

        {/* === Step 7: Supervisor Approval === */}
        {currentStep === 'supervisor_approval' && (
          <SupervisorApprovalStep
            session={session}
            isEarlyCompletion={isEarlyCompletion}
            earlyCompletionReason={earlyCompletionReason}
            setEarlyCompletionReason={setEarlyCompletionReason}
            progressPct={progressPct}
            elapsedTimeFormatted={elapsedTimeFormatted}
            boxCount={boxCount}
            pace={pace}
            initialMaterial={initialMaterial}
            totalOutput={totalOutput}
            totalWaste={totalWaste}
            remainderIntact={remainderIntact}
            remainderNotIntact={remainderNotIntact}
            finishProductNotIntact={finishProductNotIntact}
            fpNotIntactFromLot={fpNotIntactFromLot}
            byProductTotal={byProductTotal}
            yieldRate={yieldRate}
            expectedYield={expectedYield}
            hasYieldFactor={hasYieldFactor}
            isFallbackYield={isFallbackYield}
            yieldOutsideTolerance={yieldOutsideTolerance}
            yieldThreshold={yieldThreshold}
            goBack={goBack}
          />
        )}

        {/* === Submitting === */}
        {currentStep === 'submitting' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <Loader2 className="w-16 h-16 md:w-24 md:h-24 text-amber-500 animate-spin mb-6" />
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
              {t('mobile.submittingWaste') || 'Submitting waste tracking...'}
            </h2>
          </div>
        )}

        {/* === Completed === */}
        {currentStep === 'completed' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div className="w-24 h-24 md:w-28 md:h-28 mb-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <CheckCircle className="w-14 h-14 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('mobile.wasteSubmittedSuccess') || 'Session Completed!'}
            </h2>
            <p className="text-gray-500">
              {t('mobile.redirecting') || 'Redirecting...'}
            </p>
          </div>
        )}
      </div>

      {/* Side Menu */}
      {showSideMenu && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowSideMenu(false)}
          />
          {/* Menu Panel */}
          <div className="relative w-72 md:w-80 lg:w-96 max-w-[80%] bg-white dark:bg-gray-800 h-full shadow-xl flex flex-col">
            {/* Menu Header with Logo */}
            <div className="px-4 py-5 border-b border-gray-200 dark:border-gray-700">
              <img
                src="/wf-removebg-preview.png"
                alt="Way Fong"
                className="h-12 w-auto"
              />
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {/* Back to Packer View */}
              <button
                onClick={() => {
                  setShowSideMenu(false);
                  navigate('/operator/mobile-packer', {
                    state: { station, session, employee }
                  });
                }}
                className="w-full flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-3 text-gray-400" />
                {t('mobile.backToPacker') || 'Back to Packer'}
              </button>

              {/* Divider */}
              <div className="my-4 border-t border-gray-200 dark:border-gray-700" />

              {/* Language Switcher */}
              <button
                onClick={() => {
                  const langCycle: Language[] = ['en', 'zh-TW', 'es'];
                  const currentIndex = langCycle.indexOf(language);
                  const nextLang = langCycle[(currentIndex + 1) % langCycle.length];
                  setLanguage(nextLang);
                }}
                className="w-full flex items-center px-3 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Globe className="w-5 h-5 mr-3 text-gray-400" />
                <span>{t('nav.language') || 'Language'}</span>
                <span className="ml-auto text-sm font-medium text-blue-600 dark:text-blue-400">
                  {languageNames[language]}
                </span>
              </button>
            </nav>

            {/* User Info at Bottom */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold">
                  {employee.display_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{employee.display_name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">#{employee.employee_number}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileWasteTracking;
